<template>
  <div class="block-selection-panel">
    <h5>Block Selection</h5>
    
    <!-- Farm Info -->
    <div class="farm-info mb-3">
      <div class="alert alert-info">
        <strong>Farm:</strong> {{ currentFarm ? currentFarm.name : 'Loading...' }}
        <br>
        <small>Total Blocks: {{ blocks.length }}</small>
      </div>
    </div>

    <!-- Selected Block Info -->
    <div v-if="selectedBlock" class="selected-block-info mb-3">
      <div class="alert alert-success">
        <h6><i class="fas fa-check-circle"></i> Selected Block</h6>
        <strong>{{ selectedBlock.name }}</strong>
        <br>
        <small>ID: {{ selectedBlock.id }}</small>
        <br>
        <small v-if="selectedBlock.area">Area: {{ formatArea(selectedBlock.area) }}</small>
      </div>
    </div>

    <!-- Latest Imagery Info -->
    <div v-if="latestImagery" class="latest-imagery-info mb-3">
      <div class="alert alert-info">
        <h6><i :class="latestHeaderIcon"></i> {{ latestHeaderTitle }}</h6>
        <strong>{{ latestDateDisplay }}</strong>
        <br>
        <small v-if="latestKind === 'optical' && hasCloudCover">
          Cloud Cover: {{ latestImagery.cloudCover }}%
        </small>
        <small v-else-if="latestKind === 'sar'">
          Polarizations: {{ latestPolarizations }}
        </small>
        <small v-else-if="latestKind === 'landcover'">
          Product: Land cover classification
        </small>
        <small v-else-if="latestKind === 'soil'">
          Product: Gridded soil properties
        </small>
        <small v-else-if="latestKind === 'terrain'">
          Product: Digital elevation model
        </small>
        <small v-else-if="latestKind === 'climate'">
          Product: Daily climate grid
        </small>
        <small v-else-if="latestIsComposite">
          Product: Cloud-filtered composite
        </small>
        <br>
        <small>Source: {{ latestImagery.satellite || latestImagery.source || '—' }}</small>
      </div>
    </div>

    <!-- Block List -->
    <div class="block-list">
      <h6>All Blocks</h6>
      <div class="list-group">
        <a
          v-for="block in blocks"
          :key="block.id"
          @click="$emit('selectBlock', block)"
          class="list-group-item list-group-item-action clickable"
          :class="{ 'selected-block': selectedBlock && selectedBlock.id === block.id }"
        >
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <strong>{{ block.name }}</strong>
              <br>
              <small class="text-muted">ID: {{ block.id }}</small>
            </div>
            <div v-if="block.area" class="text-right">
              <small class="text-muted">{{ formatArea(block.area) }}</small>
            </div>
          </div>
        </a>
      </div>
    </div>
    
          <!-- Action Buttons -->
      <div class="mt-3">
        <button 
          v-if="selectedBlock" 
          class="btn btn-success btn-block"
          @click="$emit('browseImagery')"
          :disabled="searchingImagery"
        >
          <i class="fas fa-satellite"></i> 
          {{ searchingImagery ? 'Searching...' : 'Browse Satellite Imagery' }}
        </button>
        
        <!-- Save as Dataset Button -->
        <button 
          v-if="satelliteLayerAdded" 
          class="btn btn-primary btn-block mt-2"
          @click="$emit('saveAsDataset')"
        >
          <i class="fas fa-save"></i> 
          Save as Dataset
        </button>
      </div>
      <!-- Satellite Imagery Information -->
      <div class="mt-3">
        <div class="alert alert-info">
          <h6><i class="fas fa-satellite"></i> Satellite Imagery</h6>
          <p class="mb-2">Browse recent satellite imagery for your farm blocks. Select a scene to view it on your map.</p>
        </div>
      </div>
  </div>
</template>

<script>
export default {
  name: 'BlockSelectionPanel',
  props: {
    blocks: {
      type: Array,
      default: () => []
    },
    selectedBlock: {
      type: Object,
      default: null
    },
    currentFarm: {
      type: Object,
      default: null
    },
    latestImagery: {
      type: Object,
      default: null
    },
    searchingImagery: {
      type: Boolean,
      default: false
    },
    satelliteLayerAdded: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    latestKind() {
      const s = this.latestImagery
      if (!s) return 'optical'
      if (s.kind) return s.kind
      const meta = s.collectionMeta || s.feature?.collectionMeta
      if (meta?.kind) return meta.kind
      const coll = String(s.collection || s.feature?.collection || '').toLowerCase()
      if (coll.startsWith('sentinel-1')) return 'sar'
      if (/io-lulc|worldcover/.test(coll)) return 'landcover'
      if (/gnatsgo|ssurgo/.test(coll)) return 'soil'
      if (/3dep/.test(coll)) return 'terrain'
      if (/daymet/.test(coll)) return 'climate'
      return 'optical'
    },
    latestIsComposite() {
      const s = this.latestImagery
      if (!s) return false
      const coll = String(s.collection || s.feature?.collection || '').toLowerCase()
      if (/modis|worldcover|io-lulc|daymet|gnatsgo|3dep/.test(coll)) return true
      const props = s.properties || s.feature?.properties || {}
      return !props.datetime && (props.start_datetime || props.end_datetime)
    },
    latestDateDisplay() {
      const s = this.latestImagery
      if (!s) return ''
      // Prefer the raw properties if present (modal passes the STAC feature)
      const props = s.properties || s.feature?.properties || {}
      const fmtShort = d => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
      if (props.datetime) return this.formatDate(props.datetime)
      if (s.datetime) return this.formatDate(s.datetime)
      if (props.start_datetime && props.end_datetime) {
        return `${fmtShort(props.start_datetime)} → ${fmtShort(props.end_datetime)}`
      }
      if (props.start_datetime) return fmtShort(props.start_datetime)
      if (props.end_datetime) return fmtShort(props.end_datetime)
      return 'Unknown'
    },
    hasCloudCover() {
      const s = this.latestImagery
      if (!s) return false
      return typeof s.cloudCover === 'number' ||
        typeof (s.properties || {})['eo:cloud_cover'] === 'number'
    },
    latestPolarizations() {
      const s = this.latestImagery || {}
      const props = s.properties || s.feature?.properties || {}
      const pols = props['sar:polarizations'] || []
      return pols.length ? pols.join(' / ') : '—'
    },
    latestHeaderTitle() {
      switch (this.latestKind) {
        case 'sar': return 'Latest SAR Imagery'
        case 'landcover': return 'Latest Land Cover'
        case 'soil': return 'Latest Soil Raster'
        case 'terrain': return 'Latest Elevation Data'
        case 'climate': return 'Latest Climate Grid'
        default: return 'Latest Satellite Imagery'
      }
    },
    latestHeaderIcon() {
      switch (this.latestKind) {
        case 'sar': return 'fas fa-broadcast-tower'
        case 'landcover': return 'fas fa-map'
        case 'soil': return 'fas fa-seedling'
        case 'terrain': return 'fas fa-mountain'
        case 'climate': return 'fas fa-cloud-sun'
        default: return 'fas fa-satellite'
      }
    }
  },
  methods: {
    formatArea(area) {
      if (!area) return 'N/A'
      
      // Convert to acres if in square meters
      if (area > 1000) {
        const acres = area * 0.000247105
        return `${acres.toFixed(2)} acres`
      }
      
      return `${area.toFixed(2)} sq m`
    },

    formatDate(dateString) {
      if (!dateString) return 'Unknown'
      
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.block-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  background-color: #f8f9fa;
}

.block-list::-webkit-scrollbar {
  width: 8px;
}

.block-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.block-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.block-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.selected-block {
  background-color: #007bff !important;
  color: white !important;
  border-color: #007bff !important;
}

.selected-block:hover {
  background-color: #0056b3 !important;
  color: white !important;
}

.farm-info {
  background-color: white;
  border-radius: 0.25rem;
}

.selected-block-info {
  background-color: white;
  border-radius: 0.25rem;
}

.latest-imagery-info {
  background-color: white;
  border-radius: 0.25rem;
}

.clickable {
  cursor: pointer;
}
</style>
